package com.ives.components.image;

import com.ives.utils.math.Vector2D;
import haxe.ds.Vector;
import haxe.Timer;

import js.html.Uint8ClampedArray;
import js.html.CanvasRenderingContext2D;
import js.Browser;
import js.html.CanvasElement;
import js.html.XMLHttpRequestResponseType;
import js.html.URL;
import js.html.Blob;
import js.html.XMLHttpRequest;
import js.html.ProgressEvent;

import com.er453r.hxcompo.Component;

import com.ives.utils.Capabilities;
import com.ives.utils.Remoting.DicomImageData;
import com.ives.utils.Remoting.Histogram;
import com.ives.utils.Log;
import com.ives.utils.math.Vector3D;

class DicomImage extends Component {
	public static inline var IMAGE_LOADED:String = "IMAGE_LOADED";

	private static inline var MONOCHROME1:String = "MONOCHROME1";
	private static inline var MONOCHROME2:String = "MONOCHROME2";
	private static inline var SIGNED:UInt = 1;
	private static inline var SIGNED_LIMIT:UInt = 0x7FFF;
	private static inline var HISTOGRAM_BUCKETS:UInt = 64;

	static private inline var POSITION = "Position";
	static private inline var ROW_DIRECTION = "Row-Direction";
	static private inline var COLUMN_DIRECTION = "Column-Direction";
	static private inline var ROW_SPACING = "Row-Spacing";
	static private inline var COLUMN_SPACING = "Column-Spacing";
	static private inline var PROJECTION = "Projection";
	static private inline var RESCALE_SLOPE = "Rescale-Slope";
	static private inline var RESCALE_INTERCEPT = "Rescale-Intercept";
	static private inline var PIXEL_REPRESENTATION = "Pixel-Representation";

	public var position:Vector3D;
	public var rowDirection:Vector3D;
	public var columnDirection:Vector3D;
	public var rowSpacing:Float;
	public var columnSpacing:Float;
	private var projection:Bool;
	private var rescaleIntercept:Float;
	private var rescaleSlope:Float;
	private var pixelRepresentation:Int;

	private static inline var QUALITY:UInt = 40;

	private var baseUrl:String;
	private var pendingBaseUrl:String;
	public var loading:Bool;
	private var timer:Timer; // lossless timer
	private var request:XMLHttpRequest;

	public var x:Float = 0;
	public var y:Float = 0;
	public var scale:Float = 1;
	public var rotation:Float = 0;
	@:isVar public var mirrored(default, set):Bool;
	public var lossless:Bool;

    private var svgImage:js.html.svg.ImageElement;

	public function new(){
		request = new XMLHttpRequest();
		request.addEventListener("progress", onProgress);
		request.addEventListener("load", onLoad);
		request.addEventListener("error", onError);

		on(image, "load", onDecoded);

		svgImage = cast imageRef;

		if(Capabilities.is([Capabilities.EDGE, Capabilities.IE]))
			image.style.display = "none";
		else
			svgImage.style.display = "none";
	}

	public function getDicomCoordinates(imagePoint:Vector2D):Vector3D{
		var xDistance = imagePoint.x * columnSpacing;
		var yDistance = imagePoint.y * rowSpacing;

		return position.clone().add(columnDirection.clone().setLengthTo(xDistance)).add(rowDirection.clone().setLengthTo(yDistance));
	}

	public function getImageCoordinates(dicomPoint:Vector3D):Vector2D{
		var xDistance = dicomPoint.clone().subtract(position).dot(columnDirection);
		var yDistance = dicomPoint.clone().subtract(position).dot(rowDirection);

		return new Vector2D(xDistance / columnSpacing, yDistance / rowSpacing);
	}

	public function distanceFromSlice(dicomPoint:Vector3D):Float{
		var perpendicular = columnDirection.clone().cross(rowDirection);

		return Math.abs(dicomPoint.clone().subtract(position).dot(perpendicular));
	}

	public function volumeToSliceCoordinates(volumePoints:Array<Vector3D>):Array<Vector2D>{
		var slicePoints:Array<Vector2D> = [];

		for(volumePoint in volumePoints){
			var x = volumePoint.clone().subtract(position).dot(columnDirection);
			var y = volumePoint.clone().subtract(position).dot(rowDirection);

			slicePoints.push(new Vector2D(x, y));
		}

		return slicePoints;
	}

	private function updateFilter(){
		var filterName:String = 'dicom-image-filter-${Math.random()}';

		filter.id = filterName;

		image.style.filter = lossless ? 'url("#${filterName}")' : null;
        svgImage.style.filter = lossless ? 'url("#${filterName}")' : null;
	}

	private function onDecoded(){
		if(lossless){
			process();

			calculateHistogram();
		}

		svgImage.setAttribute("width", Std.string(image.width));
		svgImage.setAttribute("height", Std.string(image.height));
		svg.setAttribute("width", Std.string(image.width));
		svg.setAttribute("height", Std.string(image.height));

		updateFilter();

		dispatch(IMAGE_LOADED);
	}

	public function cancel(){
		request.abort();

		loading = false;
	}

	private function onProgress(event:ProgressEvent){
		dispatch("progress", event);
	}

	private function onError(){
		loading = false;

		Log.debug('Error downloading image');
	}

	private function onLoad(event):Void {
		loading = false;

		var url:String = URL.createObjectURL(new Blob([request.response]));

		this.position = Vector3D.fromString(request.getResponseHeader(POSITION));
		this.rowDirection = Vector3D.fromString(request.getResponseHeader(ROW_DIRECTION));
		this.columnDirection = Vector3D.fromString(request.getResponseHeader(COLUMN_DIRECTION));
		this.rowSpacing = Std.parseFloat(request.getResponseHeader(ROW_SPACING));
		this.columnSpacing = Std.parseFloat(request.getResponseHeader(COLUMN_SPACING));
		this.projection = request.getResponseHeader(PROJECTION) == "true";
		this.rescaleIntercept = Std.parseFloat(request.getResponseHeader(RESCALE_INTERCEPT));
		this.rescaleSlope = Std.parseFloat(request.getResponseHeader(RESCALE_SLOPE));
		this.pixelRepresentation = Std.parseInt(request.getResponseHeader(PIXEL_REPRESENTATION));

		image.src = url;

		if(Capabilities.is([Capabilities.EDGE, Capabilities.IE]))
			svgImage.href.baseVal = url;

		if(pendingBaseUrl != null){
			baseUrl = pendingBaseUrl;
			pendingBaseUrl = null;

			loadLossy();
		}

		if(!lossless){
			if(timer != null)
				timer.stop();

			if(baseUrl.indexOf("vrc") == -1 && baseUrl.indexOf("mip") == -1 && baseUrl.indexOf("mpr") == -1 && baseUrl.indexOf("crossSection") == -1)
				timer = Timer.delay(loadLossless, 500);
		}

		Log.debug('Complete (lossless: ${lossless}})');

		dispatch(IMAGE_LOADED);
	}

	public function setBaseUrl(url:String){
		if(loading){
			if(lossless)
				cancel();
			else{
				pendingBaseUrl = url;

				return;
			}
		}

		baseUrl = url;

		loadLossy();
	}

	private function loadLossy(){
		if(baseUrl == null)
			return;

		loading = true;
		lossless = false;

		var url:String = '${baseUrl}${baseUrl.indexOf("?") == -1 ? "?" : "&"}q=${QUALITY}&c=${Math.round(windowCenter)}&w=${Math.round(windowWidth)}';

		request.open("GET", url);
		request.responseType = XMLHttpRequestResponseType.ARRAYBUFFER;
		request.send();

		dispatch("loading");
	}

	private function loadLossless(){
		if(baseUrl == null)
			return;

		loading = true;
		lossless = true;

		request.open("GET", baseUrl);
		request.responseType = XMLHttpRequestResponseType.ARRAYBUFFER;
		request.send();

		dispatch("loading");
	}

	private function intesity2HU(intesity:Float):Float{
		if(pixelRepresentation == SIGNED)
			intesity -= SIGNED_LIMIT;

		return intesity * rescaleSlope + rescaleIntercept;
	}

	private function HU2intesity(HU:Float):Float{
		var value = HU - rescaleIntercept;

		if(rescaleSlope != 0)
			value /= rescaleSlope;

		if(pixelRepresentation == SIGNED)
			value += SIGNED_LIMIT;

		return value;
	}

	public var histogram:Vector<UInt>;
	public var pixels:Vector<UInt>;
	public var histogramMin:Float;
	public var histogramMax:Float;
	public var histogramWidth:Float;
	public var histogramMaxCount:UInt;

	public var remoteHistogram:Histogram;

	@:isVar public var dicomImageData(get, set):DicomImageData;
	@:isVar public var windowCenter(get, set):Float = 0;
	@:isVar public var windowWidth(get, set):Float = 0;
	@:isVar public var negative(get, set):Bool = false;

	private function getPixelsFromImage(image:js.html.ImageElement):Vector<UInt>{
		var canvas:CanvasElement = Browser.document.createCanvasElement();

		canvas.width = image.width;
		canvas.height = image.height;

		var context:CanvasRenderingContext2D = canvas.getContext2d();

		context.drawImage(image, 0, 0, image.width, image.height);

		var data:Uint8ClampedArray = context.getImageData(0, 0, image.width, image.height).data;

		pixels = new Vector(Std.int(data.length / 4));

		for(n in 0...pixels.length){
			var g:UInt = data[n * 4 + 1];
			var b:UInt = data[n * 4 + 2];

			pixels[n] = 256 * g + b;
		}

		return pixels;
	}

	public function getPixel(x:Int, y:Int):Float{
		if(x < 0 || x >= image.width || y < 0 || y >= image.height)
			return 0;
		else
			return intesity2HU(pixels[x + y * image.width]);
	}

	public function getIntensities(xFrom:UInt, yFrom:UInt, xTo:UInt, yTo:UInt):Vector<Float>{
		var values:Vector<Float> = new Vector((xTo - xFrom) * (yTo - yFrom));

		var index:UInt = 0;

		for(x in xFrom...xTo)
			for(y in yFrom...yTo)
				values[index++] = getPixel(x, y);

		return values;
	}

	private function process(){
		if(dicomImageData == null || windowWidth < 1)
			return;

		if(!(dicomImageData.photometricInterpretation == MONOCHROME1 || dicomImageData.photometricInterpretation == MONOCHROME2))
			return;

		var past:Float = Timer.stamp();

		var min:Float = HU2intesity(windowCenter - (windowWidth / 2));
		var max:Float = HU2intesity(windowCenter + (windowWidth / 2));
		var scale:Float = 255.0 / (max - min);
		var iw:Float = 1.0 / (max - min);

		var a:Float = 256.0 * scale;
		var b:Float = -iw * min;

		var matrix:Array<Float>;

		if(dicomImageData.photometricInterpretation == MONOCHROME1 && !negative || dicomImageData.photometricInterpretation != MONOCHROME1 && negative){
			matrix = [
				0,- a, -scale, 0, 1.0 - b,
				0, -a, -scale, 0, 1.0 - b,
				0, -a, -scale, 0, 1.0 - b,
				0, 0,       0, 0, 1
			];
		}
		else{
			matrix = [
				0, a, scale, 0, b,
				0, a, scale, 0, b,
				0, a, scale, 0, b,
				0, 0,     0, 0, 1
			];
		}

		colorMatrix.setAttribute('values', matrix.toString());

		updateFilter();
	}

	private function preprocess(){
		if(lossless)
			process();
	}

	private function calculateHistogram(){
		this.pixels = getPixelsFromImage(image);

		var data:Vector<Float> = getIntensities(0, 0, image.width, image.height);

		histogramMin = data[0];
		histogramMax = data[0];

		for(value in data){
			if(value < histogramMin)
				histogramMin = value;

			if(value > histogramMax)
				histogramMax = value;
		}

		var histogramBucketSize:UInt = Math.floor((histogramMax - histogramMin) / HISTOGRAM_BUCKETS);

		histogramWidth = histogramMax - histogramMin;

		histogram = new Vector<UInt>(HISTOGRAM_BUCKETS + 1);

		for(n in 0...histogram.length)
			histogram[n] = 0;

		for(value in data){
			var index:UInt = Math.floor((value - histogramMin) / histogramBucketSize);
			index = index >= histogram.length ? histogram.length - 1 : index;
			var value = histogram[index];

			histogram[index] = value + 1;
		}

		histogramMaxCount = 0;

		for(n in 1...histogram.length)
			if(histogramMaxCount < histogram[n])
				histogramMaxCount = histogram[n];
	}

	function set_dicomImageData(dicomImageData:DicomImageData) {
		windowCenter = dicomImageData.windowCenter;
		windowWidth = dicomImageData.windowWidth;

		return this.dicomImageData = dicomImageData;
	}

	function set_windowCenter(value:Float) {
		this.windowCenter = value;

		preprocess();
		
		if(!lossless)
			loadLossy();

		return value;
	}

	function get_windowCenter():Float {
		return windowCenter;
	}

	function get_windowWidth():Float {
		return windowWidth;
	}

	public function setWindow(windowWidth:Float, windowCenter:Float){
		this.windowWidth = windowWidth;
		this.windowCenter = windowCenter;

		preprocess();
	}

	function set_windowWidth(value:Float) {
		this.windowWidth = value;

		preprocess();

		if(!lossless)
			loadLossy();

		return value;
	}

	function get_negative():Bool {
		return negative;
	}

	function set_negative(value:Bool) {
		this.negative = value;

		preprocess();

		return this.negative;
	}

	function get_dicomImageData():DicomImageData {
		return dicomImageData;
	}

	public function width():Float{
		return image.width;
	}

	public function height():Float{
		return image.height;
	}

	function set_mirrored(value:Bool) {
		if(value)
			viewElement.classList.add("mirrored");
		else
			viewElement.classList.remove("mirrored");

		return this.mirrored = value;
	}
}
